import { useTranslation } from 'react-i18next';
import { ModulePlaceholderForm } from '../../components/ModulePlaceholderForm';
import { workflowService } from '@kss/api';
import type { WorkflowCreate, WorkflowEntityType, WorkflowStep } from '@kss/api';

const ENTITY_TYPES: { value: WorkflowEntityType; label: string }[] = [
  { value: 'PurchaseOrder',     label: 'Purchase order' },
  { value: 'LeaveRequest',      label: 'Leave request' },
  { value: 'ExpenseClaim',      label: 'Expense claim' },
  { value: 'DiscountOverride',  label: 'Discount override' },
  { value: 'SalaryRevision',    label: 'Salary revision' },
  { value: 'AssetAllocation',   label: 'Asset allocation' },
  { value: 'VendorOnboarding',  label: 'Vendor onboarding' },
  { value: 'CustomerOrder',     label: 'Customer order' },
  { value: 'RefundRequest',     label: 'Refund request' },
  { value: 'ReturnRequest',     label: 'Return request' },
  { value: 'Custom',            label: 'Custom' },
];

export default function NewWorkflow() {
  const { t } = useTranslation('panel');
  return (
    <ModulePlaceholderForm
      title="New workflow"
      listPath="/panel/workflows"
      tabs={[
        {
          key: 'basic',
          label: t('form.tabs.basic'),
          fields: [
            { key: 'code', label: 'Code', required: true, maxLength: 40, placeholder: 'PO_APPROVAL' },
            { key: 'name', label: 'Name', required: true, maxLength: 120 },
            {
              key: 'entityType',
              label: 'Entity type',
              type: 'select',
              required: true,
              options: ENTITY_TYPES,
            },
            {
              key: 'isActive',
              label: 'Active',
              type: 'select',
              options: [
                { value: 'true',  label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ],
            },
          ],
        },
        {
          key: 'description',
          label: t('form.tabs.details'),
          fields: [
            { key: 'description', label: 'Description', type: 'textarea' },
          ],
        },
        {
          key: 'step1',
          label: 'Step 1',
          fields: [
            { key: 's1_name', label: 'Step name', placeholder: 'Manager approval' },
            {
              key: 's1_mode',
              label: 'Mode',
              type: 'select',
              options: [
                { value: 'Sequential',  label: 'Sequential' },
                { value: 'Parallel',    label: 'Parallel' },
                { value: 'Conditional', label: 'Conditional' },
              ],
            },
            {
              key: 's1_approverType',
              label: 'Approver type',
              type: 'select',
              options: [
                { value: 'Role',       label: 'Role' },
                { value: 'User',       label: 'User' },
                { value: 'Manager',    label: 'Manager' },
                { value: 'Department', label: 'Department' },
              ],
            },
            { key: 's1_approverId', label: 'Approver id (role/user/dept)' },
            { key: 's1_thresholdField', label: 'Threshold field', placeholder: 'amount' },
            { key: 's1_thresholdMin',   label: 'Threshold min', type: 'decimal' },
            { key: 's1_thresholdMax',   label: 'Threshold max', type: 'decimal' },
            { key: 's1_slaHours',       label: 'SLA (hours)',   type: 'integer' },
            { key: 's1_escalateTo',     label: 'Escalate to (user id)' },
          ],
        },
        {
          key: 'step2',
          label: 'Step 2',
          fields: [
            { key: 's2_name', label: 'Step name (optional)', placeholder: 'Director approval' },
            {
              key: 's2_mode',
              label: 'Mode',
              type: 'select',
              options: [
                { value: 'Sequential',  label: 'Sequential' },
                { value: 'Parallel',    label: 'Parallel' },
                { value: 'Conditional', label: 'Conditional' },
              ],
            },
            {
              key: 's2_approverType',
              label: 'Approver type',
              type: 'select',
              options: [
                { value: 'Role',       label: 'Role' },
                { value: 'User',       label: 'User' },
                { value: 'Manager',    label: 'Manager' },
                { value: 'Department', label: 'Department' },
              ],
            },
            { key: 's2_approverId',     label: 'Approver id' },
            { key: 's2_thresholdField', label: 'Threshold field' },
            { key: 's2_thresholdMin',   label: 'Threshold min', type: 'decimal' },
            { key: 's2_thresholdMax',   label: 'Threshold max', type: 'decimal' },
            { key: 's2_slaHours',       label: 'SLA (hours)',   type: 'integer' },
            { key: 's2_escalateTo',     label: 'Escalate to (user id)' },
          ],
        },
        { key: 'review', label: t('form.tabs.review'), fields: [] },
      ]}
      onSave={async (state) => {
        const buildStep = (idx: 1 | 2): WorkflowStep | null => {
          const name = state[`s${idx}_name`];
          if (!name?.trim()) return null;
          return {
            stepIndex: idx,
            name: name.trim(),
            mode: (state[`s${idx}_mode`] as WorkflowStep['mode']) || 'Sequential',
            approverType: (state[`s${idx}_approverType`] as WorkflowStep['approverType']) || 'Role',
            approverId: state[`s${idx}_approverId`] || undefined,
            thresholdField: state[`s${idx}_thresholdField`] || undefined,
            thresholdMin: state[`s${idx}_thresholdMin`] ? Number(state[`s${idx}_thresholdMin`]) : undefined,
            thresholdMax: state[`s${idx}_thresholdMax`] ? Number(state[`s${idx}_thresholdMax`]) : undefined,
            slaHours: state[`s${idx}_slaHours`] ? Number(state[`s${idx}_slaHours`]) : undefined,
            escalateToUserId: state[`s${idx}_escalateTo`] || undefined,
          };
        };
        const steps = [buildStep(1), buildStep(2)].filter((s): s is WorkflowStep => s !== null);
        const payload: WorkflowCreate = {
          code: state.code,
          name: state.name,
          entityType: state.entityType as WorkflowEntityType,
          description: state.description || undefined,
          steps,
          isActive: state.isActive !== 'false',
        };
        await workflowService.create([payload]);
      }}
    />
  );
}
